/**
 * Slug validation utilities for customer slugs
 */

// Reserved slugs that cannot be used as customer identifiers
export const RESERVED_SLUGS = [
  'admin',
  'super-admin',
  'login',
  'auth',
  'dashboard',
  'debug',
  'api',
  'help',
  'support',
  'docs',
  'blog',
  'about',
  'contact',
  'pricing',
  'terms',
  'privacy',
  'settings',
  'account',
  'profile',
];

/**
 * Validates if a slug is URL-safe and not reserved
 */
export function validateSlug(slug: string): {
  valid: boolean;
  error?: string;
} {
  // Check if empty
  if (!slug || slug.trim() === '') {
    return { valid: false, error: 'Slug cannot be empty' };
  }

  // Check length
  if (slug.length < 3) {
    return { valid: false, error: 'Slug must be at least 3 characters' };
  }

  if (slug.length > 50) {
    return { valid: false, error: 'Slug must be less than 50 characters' };
  }

  // Check format (lowercase alphanumeric and hyphens only)
  const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  if (!slugRegex.test(slug)) {
    return {
      valid: false,
      error: 'Slug must be lowercase letters, numbers, and hyphens only (no spaces or special characters)',
    };
  }

  // Check if starts or ends with hyphen
  if (slug.startsWith('-') || slug.endsWith('-')) {
    return { valid: false, error: 'Slug cannot start or end with a hyphen' };
  }

  // Check if contains consecutive hyphens
  if (slug.includes('--')) {
    return { valid: false, error: 'Slug cannot contain consecutive hyphens' };
  }

  // Check if reserved
  if (RESERVED_SLUGS.includes(slug)) {
    return {
      valid: false,
      error: `"${slug}" is a reserved word and cannot be used as a slug`,
    };
  }

  return { valid: true };
}

/**
 * Converts a company name to a URL-safe slug suggestion
 */
export function generateSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single
    .slice(0, 50); // Limit length
}
